import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Globe2, PanelsTopLeft, ShieldCheck, Users, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common";
import { useApi, useQ, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { HomepageEditor, PagesEditor } from "./HomepageEditor";
import { UsersEditor } from "./UsersEditor";
import { PlansEditor } from "./PlansEditor";

export const AdminError = ({ message, retry }) => {
  const { t } = useT();
  return <div role="alert" data-testid="admin-load-error" className="rounded-lg border border-rose-500/30 bg-rose-950/20 p-6 text-sm text-rose-200">
    <p>{message}</p><Button variant="outline" onClick={retry} data-testid="admin-retry-btn" className="mt-4">{t("retry")}</Button>
  </div>;
};

const SiteEditor = ({ initialSite }) => {
  const { t, refreshSite } = useT();
  const api = useApi();
  const [draft, setDraft] = useState(initialSite);
  const [saved, setSaved] = useState(initialSite);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("homepage");
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const save = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.put("/admin/site", draft);
      setSaved(data); setDraft(data); await refreshSite(); toast.success(t("saved"));
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
    <TabsList className="h-auto w-full sm:w-auto flex flex-wrap justify-start gap-1 mb-7 bg-white/5 p-1.5" aria-label={t("admin_title")}>
      {[["homepage", Globe2], ["pages", PanelsTopLeft], ["users", Users], ["plans", CreditCard]].map(([id, Icon]) =>
        <TabsTrigger key={id} value={id} data-testid={`admin-tab-${id}`} className="gap-2 px-4 py-2 transition-colors data-[state=active]:bg-indigo-600 data-[state=active]:text-white"><Icon className="h-4 w-4" />{t(`tab_${id}`)}</TabsTrigger>)}
    </TabsList>
    <form onSubmit={save} className={tab === "homepage" || tab === "pages" ? "" : "hidden"}>
      <fieldset disabled={busy}>
        <TabsContent value="homepage" forceMount className="data-[state=inactive]:hidden"><HomepageEditor draft={draft} setDraft={setDraft} /></TabsContent>
        <TabsContent value="pages" forceMount className="data-[state=inactive]:hidden"><PagesEditor draft={draft} setDraft={setDraft} /></TabsContent>
      </fieldset>
      <div className="sticky bottom-3 z-20 mt-7 rounded-lg border border-white/15 bg-[#111118]/95 backdrop-blur-xl p-4 flex flex-wrap items-center justify-between gap-4 shadow-xl">
        <p className="text-sm text-slate-400" data-testid="admin-site-save-state" aria-live="polite">{t(busy ? "saving" : dirty ? "unsaved_changes" : "all_saved")}</p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" disabled={!dirty || busy} onClick={() => { setDraft(saved); setError(""); }} data-testid="admin-site-discard">{t("discard_changes")}</Button>
          <Button type="submit" disabled={!dirty || busy} className="bg-indigo-600 hover:bg-indigo-500" data-testid="admin-site-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{t("save_changes")}</Button>
        </div>
        {error && <p role="alert" className="w-full text-sm text-rose-300" data-testid="admin-site-error">{error}</p>}
      </div>
    </form>
    <TabsContent value="users" forceMount className="data-[state=inactive]:hidden"><UsersEditor /></TabsContent>
    <TabsContent value="plans" forceMount className="data-[state=inactive]:hidden"><PlansEditor /></TabsContent>
  </Tabs>;
};

export default function Admin() {
  const { t, site, siteError, refreshSite } = useT();
  const { data: me, error, refetch } = useQ("/admin/me", { staleTime: 0, refetchInterval: 30000, retry: false });
  if (error) return <AdminError message={errMsg(error)} retry={refetch} />;
  if (!me) return <p data-testid="admin-loading" className="text-slate-400 animate-pulse">{t("loading")}</p>;
  if (me.role !== "admin") return <div data-testid="admin-access-denied" role="alert" className="py-16"><ShieldCheck className="h-10 w-10 text-slate-500 mb-5" /><h1 className="font-display text-2xl font-bold">{t("not_admin")}</h1><Link to="/dashboard" data-testid="admin-back-dashboard" className="inline-block mt-5 text-indigo-300">{t("nav_overview")}</Link></div>;
  if (siteError && !site) return <AdminError message={t("site_load_error")} retry={refreshSite} />;
  if (!site) return <p data-testid="admin-site-loading">{t("loading")}</p>;
  return <div data-testid="admin-page">
    <PageHeader eyebrow={t("nav_admin")} title={t("admin_title")} subtitle={t("admin_sub")}
      action={<Link to="/?preview=1" target="_blank" rel="noopener noreferrer" data-testid="admin-preview-home" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5 transition-colors"><ExternalLink className="h-4 w-4" />{t("preview_home")}</Link>} />
    <SiteEditor initialSite={site} />
  </div>;
}
